import { createContext, useCallback, useContext, useState } from 'react';

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** danger=rosso, primary=verde, warning=arancione, info=blu */
  variant?: 'danger' | 'primary' | 'warning' | 'info';
  icon?: string;
}

type ConfirmCtx = (opts: ConfirmOptions) => Promise<boolean>;

const Ctx = createContext<ConfirmCtx | null>(null);

const VARIANT_STYLES = {
  danger:  { gradient: 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)', shadow: 'rgba(220, 53, 69, 0.4)', defaultIcon: '⚠️' },
  primary: { gradient: 'linear-gradient(135deg, #28a745 0%, #218838 100%)', shadow: 'rgba(40, 167, 69, 0.4)',  defaultIcon: '✓' },
  warning: { gradient: 'linear-gradient(135deg, #ffc107 0%, #e0a800 100%)', shadow: 'rgba(255, 193, 7, 0.4)',  defaultIcon: '⚠️' },
  info:    { gradient: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)', shadow: 'rgba(0, 123, 255, 0.4)',  defaultIcon: 'ℹ️' },
};

export function ConfirmProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const [resolve, setResolve] = useState<((v: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmCtx>((o) => {
    setOpts(o);
    return new Promise<boolean>((res) => setResolve(() => res));
  }, []);

  function handle(result: boolean) {
    resolve?.(result);
    setOpts(null);
    setResolve(null);
  }

  const variant = opts?.variant ?? 'primary';
  const styles = VARIANT_STYLES[variant];
  const icon = opts?.icon ?? styles.defaultIcon;

  return (
    <Ctx.Provider value={confirm}>
      {children}
      {opts && (
        <div className="modal-overlay" onClick={() => handle(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 460, padding: 0, overflow: 'hidden' }}>
            {/* Header colorato */}
            <div style={{
              background: styles.gradient,
              padding: '20px 30px',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 14,
            }}>
              <span style={{ fontSize: 32 }}>{icon}</span>
              <h3 style={{ margin: 0, fontSize: 20, color: 'white', fontWeight: 700 }}>
                {opts.title ?? 'Conferma'}
              </h3>
            </div>

            {/* Body */}
            <div style={{ padding: '24px 30px' }}>
              <p style={{ color: '#444', marginBottom: 24, fontSize: 15, lineHeight: 1.5 }}>
                {opts.message}
              </p>

              {/* Footer bottoni */}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button
                  onClick={() => handle(false)}
                  style={{
                    padding: '12px 22px', fontSize: 14, fontWeight: 600,
                    border: 'none', borderRadius: 8, cursor: 'pointer',
                    background: 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)',
                    color: 'white',
                    boxShadow: '0 2px 8px rgba(108, 117, 125, 0.3)',
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  }}
                  onMouseDown={(e) => (e.currentTarget.style.transform = 'translateY(1px)')}
                  onMouseUp={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(108, 117, 125, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(108, 117, 125, 0.3)';
                  }}>
                  {opts.cancelLabel ?? 'Annulla'}
                </button>
                <button
                  onClick={() => handle(true)}
                  autoFocus
                  style={{
                    padding: '12px 22px', fontSize: 14, fontWeight: 700,
                    border: 'none', borderRadius: 8, cursor: 'pointer',
                    background: styles.gradient,
                    color: variant === 'warning' ? '#000' : 'white',
                    boxShadow: `0 2px 8px ${styles.shadow}`,
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  }}
                  onMouseDown={(e) => (e.currentTarget.style.transform = 'translateY(1px)')}
                  onMouseUp={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = `0 6px 14px ${styles.shadow}`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = `0 2px 8px ${styles.shadow}`;
                  }}>
                  {opts.confirmLabel ?? 'Conferma'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}

export function useConfirm(): ConfirmCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useConfirm deve essere usato dentro ConfirmProvider');
  return ctx;
}
